import { PDFDocument, PDFPage, PDFImage, rgb, StandardFonts, PDFFont } from 'pdf-lib';
import QRCode from 'qrcode';

export interface SignedPDFData {
  nome_assinante: string;
  email_assinante: string;
  assinatura_base64: string;
  foto_base64?: string;
  ip: string;
  user_agent: string;
  data_assinatura: string;
  hash_pdf: string;
  token_publico: string;
}

export interface ReportPDFData {
  documento: {
    titulo: string;
    id: string;
    data_criacao: string;
    hash_original: string;
  };
  assinatura: {
    nome_assinante: string;
    email_assinante: string;
    data_assinatura: string;
    ip: string;
    user_agent: string;
    token: string;
    assinatura_base64: string;
    foto_base64?: string;
  };
  verification_url: string;
}

export class PDFGenerator {
  // Gerar PDF assinado (original + assinatura na última página)
  static async generateSignedPDF(
    originalPDFBytes: Uint8Array,
    signatureData: SignedPDFData
  ): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.load(originalPDFBytes);
    const pages = pdfDoc.getPages();
    const lastPage = pages[pages.length - 1];
    
    // Configurar fontes
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Obter dimensões da última página
    const { width, height } = lastPage.getSize();
    
    // Adicionar seção de assinatura na parte inferior da última página
    const signatureY = 100; // Posição Y para a seção de assinatura
    
    // Linha separadora
    lastPage.drawLine({
      start: { x: 50, y: signatureY + 150 },
      end: { x: width - 50, y: signatureY + 150 },
      thickness: 1,
      color: rgb(0.8, 0.8, 0.8)
    });
    
    // Título da seção de assinatura
    lastPage.drawText('ASSINATURA ELETRÔNICA', {
      x: 50,
      y: signatureY + 130,
      size: 14,
      font: boldFont,
      color: rgb(0.1, 0.4, 0.8)
    });
    
    // Informações do assinante
    lastPage.drawText('Assinante:', {
      x: 50,
      y: signatureY + 110,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    lastPage.drawText(signatureData.nome_assinante, {
      x: 120,
      y: signatureY + 110,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Data da assinatura
    lastPage.drawText('Data:', {
      x: 50,
      y: signatureY + 95,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    const dataAssinatura = new Date(signatureData.data_assinatura).toLocaleDateString('pt-BR');
    lastPage.drawText(dataAssinatura, {
      x: 120,
      y: signatureY + 95,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Adicionar imagem da assinatura
    if (signatureData.assinatura_base64) {
      try {
        const signatureImageBytes = this.base64ToUint8Array(signatureData.assinatura_base64);
        const signatureImage = await pdfDoc.embedPng(signatureImageBytes);
        lastPage.drawImage(signatureImage, {
          x: 50,
          y: signatureY + 50,
          width: 150,
          height: 40
        });
      } catch (error) {
        console.error('Erro ao adicionar assinatura:', error);
      }
    }
    
    // QR Code para verificação (canto direito)
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(`https://jusgo.com.br/verificar/${signatureData.token_publico}`);
      const qrCodeImageBytes = this.dataURLToUint8Array(qrCodeDataUrl);
      const qrCodeImage = await pdfDoc.embedPng(qrCodeImageBytes);
      lastPage.drawImage(qrCodeImage, {
        x: width - 100,
        y: signatureY + 50,
        width: 50,
        height: 50
      });
      
      lastPage.drawText('Verificar', {
        x: width - 100,
        y: signatureY + 30,
        size: 8,
        font: font,
        color: rgb(0.4, 0.4, 0.4)
      });
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
    }
    
    // Certificação legal
    lastPage.drawText('JUSGO SIGN - Assinatura Eletrônica Válida', {
      x: 50,
      y: signatureY + 20,
      size: 8,
      font: boldFont,
      color: rgb(0.1, 0.4, 0.8)
    });
    
    lastPage.drawText('Conforme MP 2.200-2/2001 e Lei 14.063/2020', {
      x: 50,
      y: signatureY + 10,
      size: 7,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    return await pdfDoc.save();
  }
  
  // Gerar PDF completo com relatório integrado
  static async generateCompletePDF(
    originalPDFBytes: Uint8Array,
    signatureData: SignedPDFData
  ): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.load(originalPDFBytes);
    const pages = pdfDoc.getPages();
    const lastPage = pages[pages.length - 1];
    
    // Configurar fontes
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Obter dimensões da última página
    const { width, height } = lastPage.getSize();
    
    // Adicionar seção de assinatura na parte inferior da última página
    const signatureY = 100; // Posição Y para a seção de assinatura
    
    // Linha separadora
    lastPage.drawLine({
      start: { x: 50, y: signatureY + 150 },
      end: { x: width - 50, y: signatureY + 150 },
      thickness: 1,
      color: rgb(0.8, 0.8, 0.8)
    });
    
    // Título da seção de assinatura
    lastPage.drawText('ASSINATURA ELETRÔNICA', {
      x: 50,
      y: signatureY + 130,
      size: 14,
      font: boldFont,
      color: rgb(0.1, 0.4, 0.8)
    });
    
    // Informações do assinante
    lastPage.drawText('Assinante:', {
      x: 50,
      y: signatureY + 110,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    lastPage.drawText(signatureData.nome_assinante, {
      x: 120,
      y: signatureY + 110,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Data da assinatura
    lastPage.drawText('Data:', {
      x: 50,
      y: signatureY + 95,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    const dataAssinatura = new Date(signatureData.data_assinatura).toLocaleDateString('pt-BR');
    lastPage.drawText(dataAssinatura, {
      x: 120,
      y: signatureY + 95,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Adicionar imagem da assinatura
    if (signatureData.assinatura_base64) {
      try {
        const signatureImageBytes = this.base64ToUint8Array(signatureData.assinatura_base64);
        const signatureImage = await pdfDoc.embedPng(signatureImageBytes);
        lastPage.drawImage(signatureImage, {
          x: 50,
          y: signatureY + 50,
          width: 150,
          height: 40
        });
      } catch (error) {
        console.error('Erro ao adicionar assinatura:', error);
      }
    }
    
    // QR Code para verificação (canto direito)
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(`https://jusgo.com.br/verificar/${signatureData.token_publico}`);
      const qrCodeImageBytes = this.dataURLToUint8Array(qrCodeDataUrl);
      const qrCodeImage = await pdfDoc.embedPng(qrCodeImageBytes);
      lastPage.drawImage(qrCodeImage, {
        x: width - 100,
        y: signatureY + 50,
        width: 50,
        height: 50
      });
      
      lastPage.drawText('Verificar', {
        x: width - 100,
        y: signatureY + 30,
        size: 8,
        font: font,
        color: rgb(0.4, 0.4, 0.4)
      });
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
    }
    
    // Certificação legal
    lastPage.drawText('JUSGO SIGN - Assinatura Eletrônica Válida', {
      x: 50,
      y: signatureY + 20,
      size: 8,
      font: boldFont,
      color: rgb(0.1, 0.4, 0.8)
    });
    
    lastPage.drawText('Conforme MP 2.200-2/2001 e Lei 14.063/2020', {
      x: 50,
      y: signatureY + 10,
      size: 7,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    // Adicionar página de relatório completo
    const reportPage = pdfDoc.addPage([595, 842]); // A4
    
    // Título do relatório
    reportPage.drawText('RELATÓRIO DE ASSINATURA ELETRÔNICA', {
      x: 50,
      y: 780,
      size: 16,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Informações detalhadas
    reportPage.drawText('Hash do Documento (SHA256):', {
      x: 50,
      y: 740,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    reportPage.drawText(signatureData.hash_pdf, {
      x: 50,
      y: 720,
      size: 8,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    // IP e User Agent
    reportPage.drawText('IP do Assinante:', {
      x: 50,
      y: 690,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    reportPage.drawText(signatureData.ip, {
      x: 150,
      y: 690,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    reportPage.drawText('Dispositivo:', {
      x: 50,
      y: 670,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    reportPage.drawText(signatureData.user_agent.substring(0, 80) + '...', {
      x: 150,
      y: 670,
      size: 8,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Foto do assinante (se disponível)
    if (signatureData.foto_base64 && signatureData.foto_base64.trim() !== '') {
      try {
        const fotoImageBytes = this.base64ToUint8Array(signatureData.foto_base64);
        const fotoImage = await pdfDoc.embedPng(fotoImageBytes);
        reportPage.drawImage(fotoImage, {
          x: 50,
          y: 500,
          width: 120,
          height: 160
        });
        
        reportPage.drawText('Foto do Assinante:', {
          x: 50,
          y: 480,
          size: 10,
          font: boldFont,
          color: rgb(0.2, 0.2, 0.2)
        });
      } catch (error) {
        console.error('Erro ao adicionar foto:', error);
      }
    } else {
      // Se não há foto, mostrar mensagem
      reportPage.drawText('Foto do Assinante: Não capturada', {
        x: 50,
        y: 480,
        size: 10,
        font: boldFont,
        color: rgb(0.6, 0.6, 0.6)
      });
    }
    
    // Certificação legal completa
    reportPage.drawText('INTEGRIDADE CERTIFICADA - JUSGO SIGN', {
      x: 50,
      y: 200,
      size: 12,
      font: boldFont,
      color: rgb(0.1, 0.4, 0.8)
    });
    
    reportPage.drawText('Assinaturas eletrônicas e físicas têm igual validade legal, conforme MP 2.200-2/2001 e Lei 14.063/2020.', {
      x: 50,
      y: 180,
      size: 9,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    reportPage.drawText('Certificado por JusGo Sign - Sistema de Assinatura Eletrônica', {
      x: 50,
      y: 160,
      size: 9,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    return await pdfDoc.save();
  }
  
  // Gerar relatório completo da assinatura (separado)
  static async generateSignatureReport(
    reportData: ReportPDFData
  ): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595, 842]); // A4
    
    // Configurar fontes
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Header
    page.drawText('RELATÓRIO DE ASSINATURAS', {
      x: 50,
      y: 800,
      size: 18,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Datas e horários em UTC-0300 (America/Sao_Paulo)', {
      x: 50,
      y: 780,
      size: 10,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    const dataAtualizacao = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    page.drawText(`Última atualização em ${dataAtualizacao}`, {
      x: 50,
      y: 765,
      size: 10,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    // Logo JusGo Sign (direita)
    page.drawText('JUSGO SIGN', {
      x: 450,
      y: 800,
      size: 14,
      font: boldFont,
      color: rgb(0.1, 0.4, 0.8)
    });
    
    page.drawText('Sistema de Assinatura Eletrônica', {
      x: 450,
      y: 785,
      size: 10,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    // QR Code para verificação
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(reportData.verification_url);
      const qrCodeImageBytes = this.dataURLToUint8Array(qrCodeDataUrl);
      const qrCodeImage = await pdfDoc.embedPng(qrCodeImageBytes);
      page.drawImage(qrCodeImage, {
        x: 450,
        y: 700,
        width: 100,
        height: 100
      });
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
    }
    
    // Informações do documento
    page.drawText('INFORMAÇÕES DO DOCUMENTO', {
      x: 50,
      y: 720,
      size: 14,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Status:', {
      x: 50,
      y: 700,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Assinado', {
      x: 120,
      y: 700,
      size: 12,
      font: font,
      color: rgb(0.2, 0.8, 0.2)
    });
    
    page.drawText('Documento:', {
      x: 50,
      y: 680,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.documento.titulo, {
      x: 150,
      y: 680,
      size: 12,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Número:', {
      x: 50,
      y: 660,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.documento.id, {
      x: 150,
      y: 660,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Data da criação:', {
      x: 50,
      y: 640,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    const dataCriacao = new Date(reportData.documento.data_criacao).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    page.drawText(dataCriacao, {
      x: 150,
      y: 640,
      size: 12,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Hash do documento original (SHA256):', {
      x: 50,
      y: 620,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.documento.hash_original, {
      x: 50,
      y: 600,
      size: 8,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    // Seção de Assinaturas
    page.drawText('ASSINATURAS', {
      x: 50,
      y: 560,
      size: 14,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('1 de 1 Assinaturas', {
      x: 450,
      y: 560,
      size: 10,
      font: font,
      color: rgb(0.4, 0.4, 0.4)
    });
    
    page.drawText('✓ Assinado via Sistema de Assinatura Eletrônica', {
      x: 50,
      y: 540,
      size: 12,
      font: font,
      color: rgb(0.2, 0.8, 0.2)
    });
    
    page.drawText('Assinado', {
      x: 450,
      y: 540,
      size: 10,
      font: font,
      color: rgb(0.2, 0.8, 0.2)
    });
    
    // Nome do assinante
    page.drawText(reportData.assinatura.nome_assinante.toUpperCase(), {
      x: 50,
      y: 510,
      size: 16,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Detalhes da assinatura
    page.drawText('Data e hora da assinatura:', {
      x: 50,
      y: 480,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    const dataAssinatura = new Date(reportData.assinatura.data_assinatura).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    page.drawText(dataAssinatura, {
      x: 220,
      y: 480,
      size: 12,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Token:', {
      x: 50,
      y: 460,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.assinatura.token, {
      x: 120,
      y: 460,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Assinatura manual
    page.drawText('Assinatura:', {
      x: 50,
      y: 430,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    if (reportData.assinatura.assinatura_base64) {
      try {
        const signatureImageBytes = this.base64ToUint8Array(reportData.assinatura.assinatura_base64);
        const signatureImage = await pdfDoc.embedPng(signatureImageBytes);
        page.drawImage(signatureImage, {
          x: 50,
          y: 350,
          width: 200,
          height: 60
        });
      } catch (error) {
        console.error('Erro ao adicionar assinatura:', error);
      }
    }
    
    page.drawText(reportData.assinatura.nome_assinante, {
      x: 50,
      y: 340,
      size: 12,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Pontos de autenticação
    page.drawText('PONTOS DE AUTENTICAÇÃO', {
      x: 50,
      y: 300,
      size: 14,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('E-mail:', {
      x: 50,
      y: 280,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.assinatura.email_assinante, {
      x: 120,
      y: 280,
      size: 12,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('IP:', {
      x: 50,
      y: 260,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.assinatura.ip, {
      x: 120,
      y: 260,
      size: 12,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Dispositivo:', {
      x: 50,
      y: 240,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.assinatura.user_agent.substring(0, 80) + '...', {
      x: 120,
      y: 240,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Seção de Anexos (Foto)
    page.drawText('ANEXOS', {
      x: 50,
      y: 200,
      size: 14,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Selfie - Foto do rosto', {
      x: 50,
      y: 180,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.assinatura.nome_assinante.toUpperCase(), {
      x: 50,
      y: 160,
      size: 12,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText('Token:', {
      x: 50,
      y: 140,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(reportData.assinatura.token, {
      x: 100,
      y: 140,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Adicionar foto do assinante
    if (reportData.assinatura.foto_base64) {
      try {
        const fotoImageBytes = this.base64ToUint8Array(reportData.assinatura.foto_base64);
        const fotoImage = await pdfDoc.embedPng(fotoImageBytes);
        page.drawImage(fotoImage, {
          x: 50,
          y: 50,
          width: 150,
          height: 200
        });
      } catch (error) {
        console.error('Erro ao adicionar foto:', error);
      }
    }
    
    page.drawText('Data e hora da validação:', {
      x: 50,
      y: 30,
      size: 10,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    page.drawText(dataAssinatura, {
      x: 200,
      y: 30,
      size: 10,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    // Footer - Certificação legal
    page.drawText('INTEGRIDADE CERTIFICADA - JUSGO SIGN', {
      x: 50,
      y: 15,
      size: 12,
      font: boldFont,
      color: rgb(0.1, 0.4, 0.8)
    });
    
    page.drawText('Assinaturas eletrônicas e físicas têm igual validade legal, conforme MP 2.200-2/2001 e Lei 14.063/2020.', {
      x: 50,
      y: 5,
      size: 8,
      font: font,
      color: rgb(0.2, 0.2, 0.2)
    });
    
    return await pdfDoc.save();
  }
  
  // Utilitários
  private static base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64.split(',')[1] || base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
  
  private static dataURLToUint8Array(dataURL: string): Uint8Array {
    const binaryString = atob(dataURL.split(',')[1]);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
}
